import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { usePageTitle } from '@utils/hooks/usePageTitle';
import Box from '@components/Atoms/Box';
import Typography from '@components/Atoms/Typography';
import ShowIf from '@components/Atoms/ShowIf';
import { Tabs, Tab, Alert, AlertTitle, Chip, Divider } from '@mui/material';
import { SharedAlgoliaProvider } from '@components/Algolia/SharedAlgoliaProvider';
import AlgoliaTable from '@components/AlgoliaIndexTable/Table';
import Button from '@components/Atoms/Button';
import ContentSelector from '@components/CoontentSelector';
import EditQuestion from '@learnosityassessment/Components/EditQuestion';
import { ItemBankWrapper } from '@learnosityassessment/Components/ItemBankHarness/styles';
import { useSearchParams, useUpdateQueryParams } from '@utils/hooks/useSearchParams';
import { useFeatureFlagEnabled } from '@components/Atoms/FeatureFlagGuard';
import history from '@utils/history';
import { useGetAllPracticeAssessmentNodes } from 'chalktalk-react/hooks/genericSkillHooks';
import CloseIcon from '@mui/icons-material/Close';
import LegacyQuestionReviewer from './LegacyQuestionReviewer';
import LegacyQuestionTable from './LegacyQuestionTable';
import NodeTree from './NodeTree';

const ADMIN_URL = process.env.REACT_APP_ADMIN_URL;
const LEARNOSITY_ADAPTIVE_ORGANISATION_ID =
  process.env.REACT_APP_LEARNOSITY_ADAPTIVE_ORGANISATION_ID;

export type Skill = { id: string; name: string; slug: string; image_url: string };

export type Lesson = {
  id: string;
  name: string;
  can_practice: boolean;
  skills: Skill[];
  legacy_practices: { id: string; question_id: string }[];
  olp_practices: { id: string; question_id: string }[];
};

export type Unit = {
  meta: { lesson_practice: boolean };
  lessons: Lesson[];
};

export type Curriculum = {
  id: number;
  adaptive_learnosity_practice_enabled: boolean;
  online_lesson_practice: boolean;
  has_exam: boolean;
};

type ContentTabKey =
  | 'adaptive_practice'
  | 'legacy_practice'
  | 'exams'
  | 'breakdown'
  | 'online_lesson_practice'
  | 'practice_tree';

const FEATURE_FLAG_CONTENT_EDITOR = 'content-editor';

const ContentPage = () => {
  usePageTitle('Content Library');
  const isAllowedToEditQuestions = useFeatureFlagEnabled({
    featureName: FEATURE_FLAG_CONTENT_EDITOR,
    suspense: true,
  });
  const { questionId, eqId, nodeTabs, keepTable } = useSearchParams();
  const updateQueryParams = useUpdateQueryParams();
  const [activeTab, setActiveTab] = useState<ContentTabKey>('breakdown');
  const [selectedCurriculum, setSelectedCurriculum] =
    useState<Curriculum | undefined>(undefined);
  const [selectedUnit, setSelectedUnit] = useState<Unit | undefined>(undefined);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | undefined>(undefined);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [totalQuestions, setTotalQuestions] = useState(0);

  useEffect(() => {
    if (!isAllowedToEditQuestions) {
      history.push('/dashboard');
    }
  }, [isAllowedToEditQuestions]);

  const handleSelectorChange = useCallback(
    ({
      curriculum,
      unit,
      lesson,
      skills,
      activeSkills,
    }: {
      curriculum?: Curriculum;
      unit?: Unit;
      lesson?: Lesson;
      skills?: Skill[];
      activeSkills?: Skill[];
    }) => {
      setSelectedCurriculum(curriculum);
      setSelectedUnit(unit);
      setSelectedLesson(lesson);
      if (skills && skills.length > 0) {
        setSelectedSkills(skills);
      } else {
        setSelectedSkills(activeSkills && activeSkills.length > 0 ? activeSkills : []);
      }
    },
    [setSelectedUnit, setSelectedLesson, setSelectedSkills],
  );

  const handleStatUpdate = useCallback(
    (stats: { nbHits: number }) => {
      setTotalQuestions(stats.nbHits);
    },
    [setTotalQuestions],
  );

  const customTabs = useMemo(
    () => (nodeTabs as unknown as string)?.split(',')?.map(Number) ?? [],
    [nodeTabs],
  );

  /**
   * We determine it the same way its determined in the student's frontend (TabNavigator.tsx)
   * Review the switching logic in chalktalk-react/components/Component/Lesson/LessonContainer/hooks/index.tsx
   * before making changes here.
   */
  const isUsingLegacyPractice = useMemo(() => {
    if (!selectedUnit) {
      return false;
    }

    const haUnitPractice = selectedUnit.meta.lesson_practice;
    const unitLessonsCanPractice = selectedUnit.lessons.some((l) => l.can_practice);
    if (selectedLesson) {
      return haUnitPractice && selectedLesson.can_practice;
    }
    return haUnitPractice && unitLessonsCanPractice;
  }, [selectedUnit, selectedLesson]);

  const { data: practiceAssessmentNodes } = useGetAllPracticeAssessmentNodes(
    selectedCurriculum?.id ?? undefined,
    isUsingLegacyPractice,
  );

  const customTabNodes = useMemo(
    () => practiceAssessmentNodes?.filter((n) => customTabs.includes(n.id)) ?? [],
    [practiceAssessmentNodes, customTabs],
  );

  /**
   * We determine it the same way its determined in the student's frontend (TabNavigator.tsx)
   * Review the switching logic in chalktalk-react/components/Component/Lesson/LessonContainer/hooks/index.tsx
   * before making changes here.
   */
  const isUsingLearnosityAdaptivePractice = useMemo(() => {
    if (!selectedCurriculum) {
      return false;
    }
    return selectedCurriculum.adaptive_learnosity_practice_enabled;
  }, [selectedCurriculum]);

  const isUsingOLP = useMemo(() => {
    if (!selectedCurriculum) {
      return false;
    }

    if (!selectedCurriculum.online_lesson_practice) {
      return false;
    }

    if (!selectedUnit) {
      return false;
    }

    if (!selectedUnit.lessons.some((l) => l.olp_practices.length > 0)) {
      return false;
    }

    return true;
  }, [selectedCurriculum, selectedUnit]);

  const availableTabs = useMemo(() => {
    const tabs: { key: ContentTabKey; label: string; custom?: boolean }[] = [];

    if (isUsingLearnosityAdaptivePractice) {
      tabs.push({ key: 'adaptive_practice', label: 'Learnosity Adaptive Practice' });
    }

    if (isUsingLegacyPractice) {
      tabs.push({ key: 'legacy_practice', label: 'Legacy Practice' });
    }

    if (isUsingOLP) {
      tabs.push({ key: 'online_lesson_practice', label: 'Online Lesson Practice' });
    }

    // if (selectedCurriculum?.has_exam) {
    //   tabs.push({ key: 'exams', label: 'Exams' });
    // }

    if (practiceAssessmentNodes && practiceAssessmentNodes.length > 0) {
      tabs.push({ key: 'practice_tree', label: 'Practice Nodes' });
    }

    tabs.push({ key: 'breakdown', label: 'Breakdown' });

    customTabNodes.forEach((t) => {
      tabs.push({ key: t.id, label: `Node: '${t.name}'`, custom: true });
    });

    return tabs;
  }, [
    customTabNodes,
    isUsingLearnosityAdaptivePractice,
    isUsingLegacyPractice,
    isUsingOLP,
    practiceAssessmentNodes,
  ]);

  useEffect(() => {
    if (!selectedUnit) {
      return;
    }
    if (availableTabs.length === 0) {
      return;
    }
    if (!availableTabs.some((t) => t.key === activeTab)) {
      setActiveTab(availableTabs[0].key);
    }
  }, [activeTab, availableTabs, selectedUnit]);

  // const nodeType = isUsingLegacyPractice ? 'assessment_nodes' : 'skills';
  const nodeType = useMemo(
    () => (isUsingLegacyPractice ? 'assessment_nodes' : 'skills'),
    [isUsingLegacyPractice],
  );

  const breakdown = useMemo(() => {
    const lessons: Lesson[] = selectedLesson
      ? [selectedLesson]
      : selectedUnit?.lessons || [];

    if (selectedSkills && selectedSkills.length > 0) {
      const selectedSkillsIds = selectedSkills.map((s) => s.id);
      return {
        lessons: lessons.filter((l) =>
          l?.[nodeType]?.some((node) => selectedSkillsIds.includes(node.id)),
        ),
        skills: selectedSkills,
      };
    }
    return { lessons, skills: selectedSkills };
  }, [selectedLesson, selectedUnit?.lessons, selectedSkills, nodeType]);

  const ReviewButton = useCallback(
    (qId: string, examQuestionId: number | undefined = undefined) => {
      if (keepTable && eqId === examQuestionId?.toString()) {
        return <Typography variant="body1">Reviewing...</Typography>;
      }
      return (
        <Button
          onClick={() => {
            updateQueryParams({ questionId: qId, eqId: examQuestionId?.toString() });
            const el = document.querySelector('#app section > div:nth-child(2)');

            if (el) {
              el.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          aria-label={`Review question ${qId}`}
        >
          Review
        </Button>
      );
    },
    [updateQueryParams, keepTable, eqId],
  );

  const handleNodeClick = useCallback(
    (node: any) => {
      const nodeId = Number(node.id);
      if (Number.isNaN(nodeId)) {
        return;
      }
      const currentNodes = [...customTabs];
      setTotalQuestions(0);
      if (!currentNodes.includes(nodeId)) {
        currentNodes.push(nodeId);
        updateQueryParams({ nodeTabs: currentNodes.join(',') });
      }
      setActiveTab(nodeId as unknown as ContentTabKey);
    },
    [updateQueryParams, customTabs, setTotalQuestions],
  );

  const handleCloseTab = useCallback(
    (nodeId: number) => {
      updateQueryParams({
        nodeTabs: customTabs.filter((id) => id !== nodeId).join(','),
      });
      setActiveTab('practice_tree');
    },
    [updateQueryParams, customTabs],
  );

  const activeCustomTab = useMemo(
    () => customTabNodes.find((t) => t.id === activeTab),
    [customTabNodes, activeTab],
  );

  const isUnitPracticeEnabled = useMemo(() => {
    if (!selectedUnit) {
      return false;
    }
    return selectedUnit?.meta?.unit_practice === true;
  }, [selectedUnit]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      flex={1}
      minHeight={0}
      overflow="auto"
      p={3}
      gap={2}
    >
      <ShowIf If={questionId}>
        <ShowIf If={isUsingLegacyPractice || eqId}>
          <LegacyQuestionReviewer
            questionId={questionId}
            examQuestionId={eqId}
            onBack={() => updateQueryParams({ questionId: '', eqId: '' })}
          />
        </ShowIf>
        <ShowIf If={!isUsingLegacyPractice && !eqId}>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              px: 2,
              py: 1,
              backgroundColor: 'white',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Button onClick={() => updateQueryParams({ questionId: '' })}>
              Back to List
            </Button>
            <Typography variant="body2">ID: {questionId}</Typography>
            <a
              href={`https://author.learnosity.com/org/${LEARNOSITY_ADAPTIVE_ORGANISATION_ID}/v1/item/editornew/?ref=${questionId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Learnosity Author
            </a>
          </Box>
          <ItemBankWrapper>
            <EditQuestion
              widgetRef={null}
              eventHandlers={null}
              referenceId={questionId}
              setIsLoading={null}
            />
          </ItemBankWrapper>
        </ShowIf>
      </ShowIf>

      {/* Content Selector */}
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          p: 2,
          visibility: !keepTable && questionId ? 'hidden' : 'visible',
        }}
      >
        <ContentSelector onChange={handleSelectorChange} />
      </Box>

      {/* Tab Content */}
      <ShowIf If={!questionId || (keepTable && questionId)}>
        <Box
          sx={{
            mt: 1,
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            p: 2,
            backgroundColor: 'white',
          }}
        >
          <ShowIf If={!selectedUnit}>
            <Typography variant="h4">Please select a unit to get started.</Typography>
          </ShowIf>
          <ShowIf If={Boolean(selectedUnit)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(_, nextValue) => {
                  setTotalQuestions(0);
                  setActiveTab(nextValue as ContentTabKey);
                }}
                aria-label="Content tabs"
              >
                {availableTabs.map((t) => (
                  <Tab
                    key={t.key}
                    value={t.key}
                    label={
                      t.custom ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          {t.label}
                          <Typography
                            component="span"
                            sx={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCloseTab(Number(t.key));
                            }}
                          >
                            <CloseIcon fontSize="small" sx={{ pt: 0.5 }} />
                          </Typography>
                        </Box>
                      ) : (
                        t.label
                      )
                    }
                  />
                ))}
              </Tabs>

              <ShowIf
                If={totalQuestions > 0 && selectedSkills && selectedSkills.length > 0}
              >
                <Typography variant="body2">Total Questions: {totalQuestions}</Typography>
              </ShowIf>
            </Box>

            <Box sx={{ pt: 2 }}>
              {/* LEARNOSITY ADAPTIVE PRACTICE */}
              <ShowIf If={activeTab === 'adaptive_practice'}>
                <Box>
                  <ShowIf If={selectedSkills && selectedSkills.length > 0}>
                    <SharedAlgoliaProvider indexName="learnosity_items">
                      <AlgoliaTable
                        keyId="question-reviewer"
                        selectedSkills={selectedSkills}
                        questionSource="chalktalk"
                        selectButton={ReviewButton}
                        statUpdate={handleStatUpdate}
                        emptyStateComponent
                      />
                    </SharedAlgoliaProvider>
                  </ShowIf>

                  <ShowIf If={selectedSkills && selectedSkills.length === 0}>
                    <Alert severity="info" variant="outlined">
                      <AlertTitle>No practices available</AlertTitle>
                      <Typography variant="body2">
                        This selection has no skills attached, so there are no practice
                        items available to show.
                      </Typography>
                    </Alert>
                  </ShowIf>
                </Box>
              </ShowIf>
              {/* LEGACY PRACTICE */}
              <ShowIf If={activeTab === 'legacy_practice'}>
                <LegacyQuestionTable
                  selectedUnit={selectedUnit}
                  selectedLesson={selectedLesson}
                  rowButton={ReviewButton}
                  statUpdate={handleStatUpdate}
                  isOLP={false}
                />
              </ShowIf>
              {/* ONLINE LESSON PRACTICE */}
              <ShowIf If={activeTab === 'online_lesson_practice'}>
                <LegacyQuestionTable
                  selectedUnit={selectedUnit}
                  selectedLesson={selectedLesson}
                  rowButton={ReviewButton}
                  statUpdate={handleStatUpdate}
                  isOLP
                />
              </ShowIf>
              {/* EXAMS */}
              {/* TODO: Add Exams after Updating Algolia with Exam Questions */}
              {/* <ShowIf If={activeTab === 'exams'}>
                <Box>
                  <EditActivity
                    widgetRef={null}
                    eventHandlers={null}
                    referenceId="(Official) E-ACT Reading Sitting 1 Placement"
                  />
                </Box> */}
              {/* </ShowIf> */}
              {/* BREAKDOWN */}
              <ShowIf If={activeTab === 'breakdown'}>
                <Box>
                  <ShowIf If={!selectedUnit}>
                    <Typography variant="body2">
                      Select a unit to see the breakdown.
                    </Typography>
                  </ShowIf>
                  <ShowIf If={Boolean(selectedUnit)}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Chip
                        label={`Lessons: ${breakdown.lessons.length}`}
                        sx={{ backgroundColor: '#F3F4F6' }}
                      />
                      <Chip
                        label={`Unique skills: ${breakdown.skills.length}`}
                        sx={{ backgroundColor: '#F3F4F6' }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {breakdown.lessons.map((lesson) => (
                        <Box
                          key={lesson.id}
                          sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            p: 1.5,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {lesson.name}
                            </Typography>
                            <a
                              href={`${ADMIN_URL}/admin/contentgroups/lessongroup/${lesson.id}/change/`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open Lesson in Django Admin
                            </a>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          {lesson?.[nodeType]?.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No skills attached to this lesson.
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                              {lesson?.[nodeType]?.map((s) => (
                                <Chip key={s.id} label={s.name} variant="outlined" />
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </ShowIf>
                </Box>
              </ShowIf>

              <ShowIf If={activeTab === 'practice_tree'}>
                <Box>
                  <ShowIf
                    If={
                      practiceAssessmentNodes &&
                      practiceAssessmentNodes.length > 0 &&
                      selectedCurriculum?.id
                    }
                  >
                    <SharedAlgoliaProvider indexName="learnosity_items">
                      <NodeTree
                        assessmentNodes={practiceAssessmentNodes ?? []}
                        selectedSkills={selectedSkills}
                        onQuestionClick={handleNodeClick}
                        curriculumId={selectedCurriculum?.id ?? 2}
                        isUsingLegacyPractice={
                          isUsingLegacyPractice && isUnitPracticeEnabled
                        }
                      />
                    </SharedAlgoliaProvider>
                  </ShowIf>
                </Box>
              </ShowIf>

              {/* NODE CUSTOM TABS */}
              {activeCustomTab && (
                <Box>
                  <Typography variant="h3" align="center">
                    All Questions Associated with AssessmentNode: ({activeCustomTab.id}) -
                    {activeCustomTab.name}
                  </Typography>
                  <Typography variant="h4" align="center">
                    Source: {isUsingLegacyPractice ? 'Chalktalk Internal' : 'Learnosity'}
                  </Typography>
                  <ShowIf
                    If={
                      isUsingLegacyPractice &&
                      selectedCurriculum?.id &&
                      activeCustomTab?.id
                    }
                  >
                    <LegacyQuestionTable
                      selectedUnit={[]}
                      selectedLesson={[]}
                      rowButton={ReviewButton}
                      statUpdate={handleStatUpdate}
                      isOLP={false}
                      assessmentNodeId={activeCustomTab.id}
                      curriculumId={selectedCurriculum?.id}
                    />
                  </ShowIf>
                  <ShowIf If={!isUsingLegacyPractice}>
                    <SharedAlgoliaProvider indexName="learnosity_items">
                      <AlgoliaTable
                        keyId="question-reviewer"
                        selectedSkills={[activeCustomTab]}
                        questionSource="chalktalk"
                        selectButton={ReviewButton}
                        statUpdate={handleStatUpdate}
                        emptyStateComponent
                      />
                    </SharedAlgoliaProvider>
                  </ShowIf>
                </Box>
              )}
            </Box>
          </ShowIf>
        </Box>
      </ShowIf>
    </Box>
  );
};

export default ContentPage;
